import { useState, useEffect, useRef, useCallback } from "react";

const platformUrl = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
function distCalc(a1, o1, a2, o2) { const R = 3959, dL = (a2 - a1) * Math.PI / 180, dO = (o2 - o1) * Math.PI / 180; const a = Math.sin(dL / 2) ** 2 + Math.cos(a1 * Math.PI / 180) * Math.cos(a2 * Math.PI / 180) * Math.sin(dO / 2) ** 2; return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); }

export default function MapWidgetPreview({ accountSlug, mapSlug, refreshKey, liveConfig }) {
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLat, setSearchLat] = useState(null);
  const [searchLng, setSearchLng] = useState(null);
  // When set, filter by bounds (wide-area searches like "California")
  // instead of the centroid + radius used for ZIP / city queries.
  const [searchBounds, setSearchBounds] = useState(null);
  const [activeBtnIdx, setActiveBtnIdx] = useState(null);
  const [selectedLocId, setSelectedLocId] = useState(null);
  const [showPanel, setShowPanel] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const mapRef = useRef(null);
  const gmapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWinRef = useRef(null);
  const aiEndRef = useRef(null);
  // Incremented every time we need a full map rebuild
  const [mapGeneration, setMapGeneration] = useState(0);
  // Flag to prevent the marker-rebuild effect from overriding center/zoom
  // right after mapGeneration builds the map with the configured view.
  const justBuiltRef = useRef(false);

  // ─── Data fetching ─────────────────────────────────────────────────
  // NOTE: We do NOT return early / unmount the map div while loading.
  // Instead we overlay a loading indicator. This keeps mapRef stable.
  useEffect(() => {
    if (!accountSlug || !mapSlug) return;
    setLoading(true); setError(null);
    fetch(`${platformUrl}/functions/v1/map-widget-data?account=${accountSlug}&map=${mapSlug}`, { headers: { Authorization: `Bearer ${anonKey}` } })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.error);
        setApiData(d);
        setSearchLat(null); setSearchLng(null); setSearchBounds(null); setActiveBtnIdx(null); setShowPanel(null);
        // Signal that the map needs to be fully rebuilt with new data
        setMapGeneration((g) => g + 1);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [accountSlug, mapSlug, refreshKey]);

  // Merge API data with liveConfig
  const data = apiData ? {
    ...apiData,
    map: {
      ...apiData.map,
      ...(liveConfig ? {
        center: liveConfig.center || apiData.map.center,
        zoom: liveConfig.zoom ?? apiData.map.zoom,
        markerColor: liveConfig.markerColor || apiData.map.markerColor,
        markerIconUrl: liveConfig.markerIconUrl ?? apiData.map.markerIconUrl,
        searchPlaceholder: liveConfig.searchPlaceholder || apiData.map.searchPlaceholder,
        searchRadius: liveConfig.searchRadius || apiData.map.searchRadius,
        toolbarButtons: liveConfig.toolbarButtons || apiData.map.toolbarButtons,
        toolbarLayout: liveConfig.toolbarLayout || apiData.map.toolbarLayout,
        customFields: liveConfig.customFields || apiData.map.customFields,
        widget: { ...apiData.map.widget, color: liveConfig.widgetColor || apiData.map.widget.color, title: liveConfig.widgetTitle || apiData.map.widget.title, logoUrl: liveConfig.widgetLogoUrl ?? apiData.map.widget.logoUrl, height: liveConfig.widgetHeight || apiData.map.widget.height },
        ai: liveConfig.aiEnabled !== undefined ? { enabled: liveConfig.aiEnabled, greeting: liveConfig.aiGreeting || apiData.map.ai?.greeting } : apiData.map.ai,
      } : {}),
    },
  } : null;

  // ─── Google Maps lifecycle ─────────────────────────────────────────
  // Rebuild the map whenever mapGeneration changes (= new data fetched).
  // This runs as an effect so it fires AFTER React has committed the DOM,
  // guaranteeing mapRef.current is a real, attached element.
  useEffect(() => {
    if (!apiData?.map?.googleMapsApiKey || !mapRef.current) return;
    const mapData = apiData.map;

    const buildMap = () => {
      // Tear down any previous instance
      for (const m of markersRef.current) m.setMap(null);
      markersRef.current = [];
      // Clear the container so Google Maps starts fresh
      if (mapRef.current) mapRef.current.innerHTML = "";

      gmapRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: mapData.center.lat, lng: mapData.center.lng },
        zoom: mapData.zoom || 4,
        mapTypeControl: true, streetViewControl: false, fullscreenControl: true,
        styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
      });
      infoWinRef.current = new window.google.maps.InfoWindow();

      // Build markers immediately with the fresh data.
      // skipFitBounds=true because the map was just created with the
      // configured center/zoom — we don't want fitBounds overriding it.
      buildMarkers(apiData, null, null, null, null, true);
      // Prevent the marker-rebuild effect from immediately re-running
      // fitBounds when it sees the new `data` reference.
      justBuiltRef.current = true;
    };

    if (window.google?.maps) {
      buildMap();
    } else {
      const cb = `__cfP${Date.now()}`;
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${mapData.googleMapsApiKey}&libraries=places&callback=${cb}`;
      s.async = true;
      window[cb] = () => { buildMap(); delete window[cb]; };
      document.head.appendChild(s);
    }
  }, [mapGeneration]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build markers — extracted so it can be called from both buildMap and effects
  const buildMarkers = useCallback((sourceData, filterIdx, sLat, sLng, sBounds, skipFitBounds = false) => {
    if (!gmapRef.current || !sourceData) return;
    for (const m of markersRef.current) m.setMap(null);
    markersRef.current = [];

    const mapCfg = sourceData.map;
    const C = mapCfg.widget?.color || "#e63946";
    let locs = [...sourceData.locations];

    // Apply filter
    const buttons = mapCfg.toolbarButtons || [];
    if (filterIdx !== null && filterIdx !== undefined && buttons[filterIdx]?.action === "filter" && buttons[filterIdx]?.filterKey) {
      const btn = buttons[filterIdx];
      locs = locs.filter((l) => {
        if (btn.filterKey === "location_type") return l.type === btn.filterValue;
        const val = l.attributes?.[btn.filterKey];
        if (btn.filterValue === "true" || btn.filterValue === true) return val === true || val === "true";
        if (btn.filterValue === "false") return !val || val === false || val === "false";
        return String(val) === String(btn.filterValue);
      });
    }

    // Apply search filter — bounds take precedence over centroid+radius
    if (sBounds) {
      locs = locs.filter((l) => l.lat != null && l.lng != null && sBounds.contains(new window.google.maps.LatLng(l.lat, l.lng)));
    } else if (sLat != null && sLng != null) {
      locs = locs.map((l) => ({ ...l, _dist: distCalc(sLat, sLng, l.lat, l.lng) }));
      locs = locs.filter((l) => l._dist <= (mapCfg.searchRadius || 100));
      locs.sort((a, b) => a._dist - b._dist);
    }

    locs.forEach((loc, idx) => {
      if (!loc.lat || !loc.lng) return;
      const m = new window.google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng }, map: gmapRef.current, title: loc.name,
        label: { text: String(idx + 1), color: "#fff", fontSize: "11px", fontWeight: "700" },
        icon: mapCfg.markerIconUrl
          ? { url: mapCfg.markerIconUrl, scaledSize: new window.google.maps.Size(32, 32) }
          : { path: "M12 0C5.4 0 0 5.4 0 12c0 8.4 12 24 12 24s12-15.6 12-24C24 5.4 18.6 0 12 0z", fillColor: mapCfg.markerColor || C, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2, scale: 1.3, anchor: new window.google.maps.Point(12, 36), labelOrigin: new window.google.maps.Point(12, 12) },
      });
      m.addListener("click", () => {
        const e = (s) => { const d = document.createElement("div"); d.textContent = s || ""; return d.innerHTML; };
        infoWinRef.current.setContent(`<div style="font-family:-apple-system,sans-serif;max-width:260px;padding:4px;"><strong>${e(loc.name)}</strong><div style="color:#666;font-size:12px;margin:4px 0;">${e(loc.address)}</div>${loc.phone ? `<div style="font-size:12px;">📞 ${e(loc.phone)}</div>` : ""}<div style="display:flex;gap:6px;margin-top:8px;">${loc.website ? `<a href="${e(loc.website)}" target="_blank" style="padding:5px 12px;background:${C};color:#fff;border-radius:4px;font-size:11px;font-weight:600;text-decoration:none;">Website</a>` : ""}<a href="https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}" target="_blank" style="padding:5px 12px;background:#555;color:#fff;border-radius:4px;font-size:11px;font-weight:600;text-decoration:none;">Directions</a></div></div>`);
        infoWinRef.current.open(gmapRef.current, m);
        setSelectedLocId(loc.id);
      });
      markersRef.current.push(m);
    });

    if (markersRef.current.length > 0 && sLat == null && !sBounds && !skipFitBounds) {
      const b = new window.google.maps.LatLngBounds();
      markersRef.current.forEach((m) => b.extend(m.getPosition()));
      gmapRef.current.fitBounds(b);
    }
  }, []);

  // Rebuild markers when filter or search changes.
  // IMPORTANT: We never fitBounds here. The mapGeneration effect handles
  // initial marker placement with the configured center/zoom. This effect
  // only re-filters markers for user-initiated filter/search actions.
  // fitBounds is called explicitly only when a search produces results.
  useEffect(() => {
    if (!gmapRef.current || !data) return;
    // If the mapGeneration effect just built everything, skip this run.
    if (justBuiltRef.current) { justBuiltRef.current = false; return; }
    buildMarkers(data, activeBtnIdx, searchLat, searchLng, searchBounds, true);
    // If user did a centroid search (not bounds), fit to the matched results.
    if (searchLat != null && searchLng != null && markersRef.current.length > 0) {
      const b = new window.google.maps.LatLngBounds();
      markersRef.current.forEach((m) => b.extend(m.getPosition()));
      gmapRef.current.fitBounds(b);
    }
  }, [activeBtnIdx, searchLat, searchLng, searchBounds, data, buildMarkers]);

  // ─── Live config center/zoom ───────────────────────────────────────
  useEffect(() => {
    if (!liveConfig?.center || !gmapRef.current) return;
    gmapRef.current.setCenter({ lat: liveConfig.center.lat, lng: liveConfig.center.lng });
    if (liveConfig.zoom) gmapRef.current.setZoom(liveConfig.zoom);
  }, [liveConfig?._ts]);

  // Modal closed — force tile recovery
  useEffect(() => {
    if (liveConfig !== null) return;
    const gmap = gmapRef.current;
    if (!gmap) return;
    const recover = () => {
      window.google?.maps?.event?.trigger(gmap, "resize");
      const c = gmap.getCenter();
      if (c) gmap.setCenter(c);
    };
    requestAnimationFrame(() => {
      recover();
      setTimeout(recover, 200);
      setTimeout(recover, 600);
    });
  }, [liveConfig]);

  // ─── Filter locations for the list (pure, no side effects) ─────────
  const getDisplayLocs = useCallback(() => {
    if (!data) return [];
    let locs = [...data.locations];
    const buttons = data.map.toolbarButtons || [];
    if (activeBtnIdx !== null && buttons[activeBtnIdx]?.action === "filter" && buttons[activeBtnIdx]?.filterKey) {
      const btn = buttons[activeBtnIdx];
      locs = locs.filter((l) => {
        if (btn.filterKey === "location_type") return l.type === btn.filterValue;
        const val = l.attributes?.[btn.filterKey];
        if (btn.filterValue === "true" || btn.filterValue === true) return val === true || val === "true";
        if (btn.filterValue === "false") return !val || val === false || val === "false";
        return String(val) === String(btn.filterValue);
      });
    }
    if (searchBounds) {
      // Wide-area search (state/country): keep everything inside the viewport.
      locs = locs.filter((l) => l.lat != null && l.lng != null && searchBounds.contains(new window.google.maps.LatLng(l.lat, l.lng)));
      locs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (searchLat != null && searchLng != null) {
      locs = locs.map((l) => ({ ...l, _dist: distCalc(searchLat, searchLng, l.lat, l.lng) }));
      locs = locs.filter((l) => l._dist <= (data.map.searchRadius || 100));
      locs.sort((a, b) => a._dist - b._dist);
    }
    return locs;
  }, [data, activeBtnIdx, searchLat, searchLng, searchBounds]);

  // Search
  // For wide-area geocode results (state, country), use the geocode's
  // viewport bounds instead of a centroid + radius — otherwise every
  // dealer falls outside the radius from the centroid and markers vanish.
  const doSearch = () => {
    if (!searchQuery.trim() || !window.google?.maps) return;
    new window.google.maps.Geocoder().geocode({ address: searchQuery }, (res, st) => {
      if (st !== "OK" || !res[0]) return;
      const r0 = res[0];
      const types = r0.types || [];
      const isWideArea = types.some((t) => ["administrative_area_level_1", "administrative_area_level_2", "country"].includes(t));
      if (isWideArea && r0.geometry?.viewport) {
        setSearchBounds(r0.geometry.viewport);
        setSearchLat(null); setSearchLng(null);
        gmapRef.current?.fitBounds(r0.geometry.viewport);
      } else {
        setSearchBounds(null);
        const lat = r0.geometry.location.lat(), lng = r0.geometry.location.lng();
        setSearchLat(lat); setSearchLng(lng);
        gmapRef.current?.panTo({ lat, lng });
        gmapRef.current?.setZoom(types.includes("locality") ? 10 : 11);
      }
    });
  };
  const doReset = () => {
    setSearchQuery(""); setSearchLat(null); setSearchLng(null); setSearchBounds(null);
    setActiveBtnIdx(null); setSelectedLocId(null); setShowPanel(null); setShowAI(false); setAiMessages([]);
    if (gmapRef.current && data) { gmapRef.current.panTo({ lat: data.map.center.lat, lng: data.map.center.lng }); gmapRef.current.setZoom(data.map.zoom || 4); }
  };
  const panTo = (loc) => { if (gmapRef.current && loc.lat && loc.lng) { gmapRef.current.panTo({ lat: loc.lat, lng: loc.lng }); gmapRef.current.setZoom(12); const m = markersRef.current.find((x) => x.getTitle() === loc.name); if (m) window.google.maps.event.trigger(m, "click"); setSelectedLocId(loc.id); } };

  // AI chat
  const geocodeQuery = async (text) => {
    if (!window.google?.maps) return null;
    const geocoder = new window.google.maps.Geocoder();
    const tryGeocode = (addr) => new Promise((resolve) => {
      geocoder.geocode({ address: addr }, (results, status) => {
        resolve(status === "OK" && results[0] ? results[0] : null);
      });
    });
    const cleaned = text.replace(/^(i'm |i am |we're |we are |find |show |search |look for |looking for |dealers? |locations? |service centers? |shops? |stores? )(in |near |around |close to |by |through |driving through |heading to )?/i, "").trim();
    let result = await tryGeocode(cleaned);
    if (result) return result;
    result = await tryGeocode(cleaned + ", USA");
    if (result) return result;
    result = await tryGeocode(cleaned + ", Canada");
    if (result) return result;
    result = await tryGeocode(text);
    return result;
  };

  const sendAI = async () => {
    const q = aiInput.trim(); if (!q || aiLoading || !data) return;
    setAiMessages((p) => [...p, { role: "user", content: q }]); setAiInput(""); setAiLoading(true);
    try {
      let lat = data.map.center.lat, lng = data.map.center.lng;
      let geoSuccess = false, geoName = q;
      const geoResult = await geocodeQuery(q);
      if (geoResult) {
        lat = geoResult.geometry.location.lat();
        lng = geoResult.geometry.location.lng();
        geoSuccess = true;
        geoName = geoResult.formatted_address || q;
        const types = geoResult.types || [];
        var zoomLevel = types.includes("administrative_area_level_1") || types.includes("country") ? 6 : types.includes("locality") ? 9 : 8;
      }
      const isWideArea = geoResult?.types?.some((t) => ["administrative_area_level_1", "country"].includes(t));
      const radius = isWideArea ? Math.max(data.map.searchRadius || 200, 500) : (data.map.searchRadius || 200);
      let nearby = data.locations
        .filter((l) => l.lat && l.lng)
        .map((l) => ({ ...l, _dist: distCalc(lat, lng, l.lat, l.lng) }))
        .filter((l) => l._dist <= radius)
        .sort((a, b) => a._dist - b._dist)
        .slice(0, 10);
      let answer;
      if (nearby.length > 0) {
        answer = `I found ${nearby.length} location${nearby.length > 1 ? "s" : ""} near ${geoName}:\n\n` +
          nearby.map((l, i) => `**${i + 1}. ${l.name}**\n${l.address}\n${l.phone ? `📞 ${l.phone}` : ""}\n📍 ${l._dist.toFixed(1)} miles away`).join("\n\n");
        setSearchLat(lat); setSearchLng(lng); setSearchBounds(null);
        gmapRef.current?.setCenter({ lat, lng });
        gmapRef.current?.setZoom(geoSuccess ? (zoomLevel || 9) : 5);
      } else {
        answer = geoSuccess
          ? `I couldn't find any locations within ${radius} miles of ${geoName}. Try a different area or a more specific location.`
          : `I wasn't able to identify a location from "${q}". Try something like "dealers near Portland, OR" or just a city/state name like "Alaska" or "Washington".`;
      }
      setAiMessages((p) => [...p, { role: "assistant", content: answer }]);
    } catch (err) { setAiMessages((p) => [...p, { role: "assistant", content: `Sorry, something went wrong: ${err.message}` }]); }
    setAiLoading(false);
  };
  useEffect(() => { aiEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [aiMessages]);

  // ─── Render ────────────────────────────────────────────────────────
  // CRITICAL: We always render the full widget DOM including the map div.
  // Loading and error states are shown as overlays, NOT conditional returns,
  // so that mapRef never goes stale.

  if (error && !data) return <div style={{ padding: 20, color: "#f87171", fontSize: 13 }}>Preview error: {error}</div>;
  if (!data && !loading) return null;

  // While we have no data yet (first load), show a simple placeholder
  if (!data) return (
    <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #2a2a3a", background: "#fff", height: 400, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: "#555", fontSize: 13 }}>Loading preview...</div>
    </div>
  );

  const C = data.map.widget.color || "#e63946";
  const displayLocs = getDisplayLocs();
  const buttons = data.map.toolbarButtons || [];
  const layout = data.map.toolbarLayout || [{ type: "filters", position: "left" }, { type: "search", position: "right" }, { type: "reset", position: "right" }];

  const handleButtonClick = (btn, idx) => { if (btn.action === "link" && btn.url) { window.open(btn.url, "_blank"); return; } if (btn.action === "panel") { setShowPanel(showPanel === idx ? null : idx); setActiveBtnIdx(null); setShowAI(false); return; } setShowPanel(null); setShowAI(false); setActiveBtnIdx(activeBtnIdx === idx ? null : idx); };

  const renderToolbar = (pos) => layout.filter((l) => l.position === pos).flatMap((item) => {
    if (item.type === "filters") return buttons.map((btn, i) => <button key={`b${i}`} onClick={() => handleButtonClick(btn, i)} style={{ padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "none", background: (activeBtnIdx === i || showPanel === i) ? (btn.color || C) : `${btn.color || C}20`, color: (activeBtnIdx === i || showPanel === i) ? (btn.textColor || "#fff") : (btn.color || C), textTransform: "uppercase", transition: "all 0.15s" }}>{btn.label}</button>);
    if (item.type === "search") return [<input key="si" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder={data.map.searchPlaceholder || "ZIP or city"} style={{ padding: "7px 12px", border: "1px solid #ddd", borderRadius: 6, fontSize: 12, outline: "none", fontFamily: "inherit", width: 200 }} />, <button key="sb" onClick={doSearch} style={{ padding: "7px 16px", background: C, border: "none", borderRadius: 6, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", textTransform: "uppercase" }}>Search</button>];
    if (item.type === "reset") return [<button key="rb" onClick={doReset} style={{ padding: "7px 16px", background: "#666", border: "none", borderRadius: 6, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", textTransform: "uppercase" }}>Reset</button>];
    if (item.type === "ai" && data.map.ai?.enabled) return [<button key="ai" onClick={() => { setShowAI(!showAI); setShowPanel(null); setActiveBtnIdx(null); }} style={{ padding: "7px 16px", background: showAI ? C : "#fff", border: `2px solid ${C}`, borderRadius: 6, color: showAI ? "#fff" : C, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✨ AI Assistant</button>];
    return [];
  });

  const activePanel = showPanel !== null ? buttons[showPanel] : null;
  // The title above the location list reflects whichever toolbar button is
  // currently driving the view: a filter button OR a panel button. Without
  // this, clicking RV Dealers (filter) leaves the title showing the map's
  // default — looks like nothing happened to the user.
  const activeBtn =
    showPanel !== null ? buttons[showPanel] :
    activeBtnIdx !== null ? buttons[activeBtnIdx] :
    null;

  return (
    <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #2a2a3a", background: "#fff", position: "relative" }}>
      {/* Loading overlay — sits on top but does NOT unmount children */}
      {loading && (
        <div style={{ position: "absolute", inset: 0, zIndex: 10, background: "rgba(255,255,255,0.85)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ color: "#555", fontSize: 13 }}>Refreshing...</div>
        </div>
      )}

      <div style={{ padding: "10px 14px", background: "#fff", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>{renderToolbar("left")}<div style={{ flex: 1 }} />{renderToolbar("right")}</div>

      <div style={{ display: "flex", height: data.map.widget.height || "500px" }}>
        <div style={{ width: 360, display: "flex", flexDirection: "column", borderRight: "1px solid #eee", flexShrink: 0 }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
            {data.map.widget.logoUrl && <img src={data.map.widget.logoUrl} alt="" style={{ height: 28, marginBottom: 6, display: "block" }} />}
            <div style={{ fontSize: 15, fontWeight: 700, color: "#333" }}>{showAI ? "AI Assistant" : activeBtn ? activeBtn.label : data.map.widget.title}</div>
            {!showAI && !activePanel && <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>{displayLocs.length} location{displayLocs.length !== 1 ? "s" : ""}{(searchLat != null || searchBounds) ? " found" : ""}</div>}
          </div>

          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {showAI ? (<>
              <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
                {aiMessages.length === 0 && <div style={{ textAlign: "center", padding: "28px 0", color: "#bbb", fontSize: 12 }}>{data.map.ai?.greeting || "Ask me about nearby locations!"}</div>}
                {aiMessages.map((msg, i) => (<div key={i} style={{ marginBottom: 10 }}>{msg.role === "user" ? <div style={{ display: "flex", justifyContent: "flex-end" }}><div style={{ maxWidth: "85%", padding: "8px 12px", background: C, color: "#fff", borderRadius: "12px 12px 2px 12px", fontSize: 13 }}>{msg.content}</div></div> : <div style={{ padding: "10px 12px", background: "#f5f5f5", borderRadius: "12px 12px 12px 2px", fontSize: 13, color: "#333", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{msg.content}</div>}</div>))}
                {aiLoading && <div style={{ padding: 8, color: "#999", fontSize: 12 }}>Searching locations...</div>}
                <div ref={aiEndRef} />
              </div>
              <div style={{ padding: "10px 14px", borderTop: "1px solid #eee", display: "flex", gap: 6, flexShrink: 0 }}>
                <input value={aiInput} onChange={(e) => setAiInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendAI()} placeholder='Try "dealers near Portland, OR"' style={{ flex: 1, padding: "8px 12px", border: "1px solid #ddd", borderRadius: 6, fontSize: 12, outline: "none" }} />
                <button onClick={sendAI} disabled={aiLoading || !aiInput.trim()} style={{ padding: "8px 14px", background: C, border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: aiLoading || !aiInput.trim() ? 0.5 : 1 }}>Ask</button>
              </div>
            </>) : activePanel ? (
              (activePanel.panelItems || []).filter((p) => p.name).map((item, idx) => (<div key={idx} style={{ padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}><div style={{ fontSize: 15, fontWeight: 700, color: "#333" }}>{item.name}</div>{item.phone && <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>Phone: {item.phone}</div>}{item.website && <a href={item.website} target="_blank" rel="noopener" style={{ display: "block", marginTop: 8, padding: "8px 0", background: C, color: "#fff", borderRadius: 4, fontSize: 12, fontWeight: 700, textDecoration: "none", textAlign: "center", textTransform: "uppercase" }}>Website</a>}</div>))
            ) : displayLocs.length === 0 ? (
              <div style={{ padding: "40px 14px", textAlign: "center", color: "#bbb", fontSize: 12 }}>{
                (searchLat || searchBounds) ? "No locations in this area." :
                activeBtnIdx !== null ? "No locations match this filter." :
                "Search to find nearby locations."
              }</div>
            ) : displayLocs.slice(0, 100).map((loc, idx) => (
              <div key={loc.id} onClick={() => panTo(loc)} style={{ padding: "12px 14px", borderBottom: "1px solid #f0f0f0", cursor: "pointer", background: selectedLocId === loc.id ? `${C}08` : "", borderLeft: selectedLocId === loc.id ? `3px solid ${C}` : "3px solid transparent" }} onMouseEnter={(e) => { if (selectedLocId !== loc.id) e.currentTarget.style.background = "#fafafa"; }} onMouseLeave={(e) => { if (selectedLocId !== loc.id) e.currentTarget.style.background = ""; }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: C, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{idx + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>{loc.name}</div>
                    <div style={{ fontSize: 12, color: "#888", marginTop: 1 }}>{loc.address}</div>
                    {loc.phone && <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>📞 {loc.phone}</div>}
                    {(() => { const b = []; if (loc.type) b.push(loc.type); for (const cf of (data.map.customFields || [])) { const v = loc.attributes?.[cf.key]; if (cf.type === "boolean" && (v === true || v === "true")) b.push(cf.label); else if (v && cf.type !== "boolean") b.push(String(v)); } return b.length > 0 ? <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>{b.map((x, bi) => <span key={bi} style={{ padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: `${C}12`, color: C, border: `1px solid ${C}25` }}>{x}</span>)}</div> : null; })()}
                    {loc._dist != null && <div style={{ fontSize: 12, fontWeight: 700, color: C, marginTop: 4 }}>{loc._dist.toFixed(1)} mi</div>}
                    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                      {loc.website && <a href={loc.website} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} style={{ padding: "5px 14px", background: C, color: "#fff", borderRadius: 4, fontSize: 11, fontWeight: 600, textDecoration: "none" }}>Website</a>}
                      {loc.lat && loc.lng && <a href={`https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} style={{ padding: "5px 14px", background: "#555", color: "#fff", borderRadius: 4, fontSize: 11, fontWeight: 600, textDecoration: "none" }}>Directions</a>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ padding: "4px 14px", borderTop: "1px solid #f0f0f0", fontSize: 9, color: "#ccc", textAlign: "center", flexShrink: 0 }}>Powered by Clarifico AI</div>
        </div>

        <div ref={mapRef} style={{ flex: 1, minWidth: 0, background: "#e8e8e8" }}>
          {!data.map.googleMapsApiKey && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#999", fontSize: 13, padding: 20, textAlign: "center" }}>Google Maps API key not configured.</div>}
        </div>
      </div>
    </div>
  );
}
